import { useEffect, useRef, useState } from "preact/hooks";
import { Scope } from "../../components";
import { FLUENCY_REPS, TWISTER_REPS } from "../../content/drills";
import type { Card } from "../../content/types";
import { isVoiced, type MicSession } from "../../audio/mic";
import { isSpeechRecognitionSupported, scoreTranscript, startContinuousRecognition, type SpeechSession } from "../../audio/speech";
import { profile } from "../../state/store";
import type { MicState } from "../useMic";

type PassProps = {
  content: Card;
  wpm: number;
  kind: "twister" | "phrase";
  onDone: (voiceScore?: number) => void;
  mic: MicSession | null;
  micState: MicState;
  micThreshold: number;
};

export function Pass({ content, wpm, kind, onDone, mic, micState, micThreshold }: PassProps) {
  const reps = kind === "twister" ? TWISTER_REPS : FLUENCY_REPS;
  const words = content.text.split(/\s+/).length;
  const passMs = Math.max(kind === "twister" ? 3000 : 2000, (words / wpm) * 60000);
  const [rep, setRep] = useState(1);
  const [progress, setProgress] = useState(0);
  const voicedSamples = useRef({ voiced: 0, total: 0 });
  const micReady = micState === "ready" ? mic : null;
  const speechRef = useRef<SpeechSession | null>(null);
  const transcribeCoachOn = profile.value.settings.transcribeCoach;

  useEffect(() => {
    if (!transcribeCoachOn || !isSpeechRecognitionSupported()) return;
    speechRef.current = startContinuousRecognition(content.lang);
    return () => speechRef.current?.stop();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setProgress(0);
    const start = Date.now();
    const tick = setInterval(() => {
      const e = Date.now() - start;
      setProgress(Math.min(1, e / passMs));
      if (e >= passMs) {
        clearInterval(tick);
        if (rep >= reps) {
          const speech = speechRef.current;
          if (speech) {
            const transcript = speech.getTranscript();
            speech.stop();
            if (transcript.trim()) {
              onDone(scoreTranscript(content.text, transcript).accuracy);
              return;
            }
          }
          const s = voicedSamples.current;
          const score = micReady && s.total > 0 ? Math.round((s.voiced / s.total) * 100) : undefined;
          onDone(score);
        } else setRep((r) => r + 1);
      }
    }, 50);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, [rep]);

  useEffect(() => {
    if (!micReady) return;
    let raf = 0;
    function poll() {
      const s = voicedSamples.current;
      s.total += 1;
      if (isVoiced(micReady!, micThreshold)) s.voiced += 1;
      raf = requestAnimationFrame(poll);
    }
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, [micReady, micThreshold]);

  return (
    <div class="stage stage-pass">
      <p class="stage-kicker">
        Rep {rep} of {reps}
      </p>
      <div class="display stage-lead">{content.text}</div>
      <div class="pacer">
        <div class="pacer-fill" style={{ width: `${progress * 100}%` }} />
      </div>
      {micReady && <Scope getTimeDomain={() => micReady.timeDomain()} width={240} height={44} />}
    </div>
  );
}
